import { Controller } from "@hotwired/stimulus"
import { SlickGrid } from "slickgrid"
import Plotly from "plotly.js-dist"

export default class extends Controller {
  static targets = [
    "imchart", "imiChart", "marketStartDate", "marketEndDate",
    "cumulativeChart", "cumulativeStartDate", "cumulativeEndDate",
    "issuanceChart", "issuanceStartDate", "issuanceEndDate",
    "maturitiesChart", "maturitiesStartDate", "maturitiesEndDate",
    "incomeStartDate", "incomeEndDate", "incomeChart",
    "recentIssuancesTable"
  ]

  connect() {
    this.chartDataCache = {}
    this.updateChartTimeout = null
    this.indicesLoaded = false
    this.indicesData = []
    this.marketTabLoaded = false
    this.incomeTabLoaded = false
    this.maturitiesTabLoaded = false
    this.issuanceTabLoaded = false
    this.recentIssuancesGrid = false
    this.colours = {
      BLUE: 'rgb(13, 31, 45)',        // Blue
      RED: 'rgb(230, 107, 63)',       // Red
      GREEN: 'rgb(0, 109, 119)',      // Green
      GREY: 'rgb(84, 106, 123)',      // Grey
      BEIGE: 'rgb(228, 195, 173)',    // Beige
      LIGHT_BLUE: '#0d1f2d',          // Light Blue
      LIGHTER_GREY: '#799ab3',        // Lighter Grey
      LIGHT_GREY: 'rgb(102, 96, 96)'  // Light Grey
    }
    this.colorShades = [
        'rgb(226, 149, 120)',
        'rgb(0, 109, 119)',
        'rgb(131, 197, 190)', 
        'rgb(13, 31, 45)', 
        'rgb(84, 106, 123)',
        'rgb(228, 195, 173)',
        'rgb(158, 163, 176)'
    ]

    this.boundHandleTabChange = this.handleTabChange.bind(this)
    this.element.addEventListener('uikit--tab:tabChanged', this.boundHandleTabChange)
    
    this.initializeChart(this.imchartTarget)
    this.initializeChart(this.imiChartTarget)
    this.loadInitialData()
    this.setupChartModeBarStyle()
  }

  disconnect() {
    this.element.removeEventListener('uikit--tab:tabChanged', this.boundHandleTabChange)
  }

  setupChartModeBarStyle(){
    const style = document.createElement('style')
    style.id = 'quant-supply-demands-styles'
    style.textContent = `
        .modebar-group {
            display: flex !important;
            flex-direction: row !important;
            flex-wrap: nowrap !important;
            align-items: center !important;
        }
    `
    document.head.appendChild(style)
}

  handleTabChange(event) {
    const tabId = event.detail.tabId  
    this.loadInitialData()
    switch(tabId) {
      case 'market':
        if (!this.marketTabLoaded) {
        this.initializeChart(this.imchartTarget, 'market')
        this.initializeChart(this.imiChartTarget, 'market')
        this.marketTabLoaded = true
        }
        break
      case 'issuance':
        if (!this.issuanceTabLoaded) {
        this.initializeChart(this.issuanceChartTarget, 'issuance')
        this.initializeCumulativeChart()
        this.issuanceTabLoaded = true
        }
        break
      case 'maturities':
        if (!this.maturitiesTabLoaded) {
        this.initializeChart(this.maturitiesChartTarget, 'maturities')
        this.maturitiesTabLoaded = true
        }
        break
      case 'income':
        if (!this.incomeTabLoaded) {
        this.initializeChart(this.incomeChartTarget, 'income')
        this.incomeTabLoaded = true
        }
        break
      default:
        break
    }
  }

  initializeChart(chartTarget,chatType) {
    if (!chartTarget || typeof Plotly === 'undefined') return
    const layout = this.getBarChartLayout('', 'Date', 'Size', chatType)
    const finalLayout = {
      ...layout,
      annotations: [{ 
        x: 0.5, y: 0.5, xref: 'paper', yref: 'paper', 
        text: 'Select an index to view data', 
        showarrow: false, font: { size: 16, color: 'gray' } 
      }]
    }

    try {
      Plotly.newPlot(chartTarget, [], finalLayout, { responsive: true })
    } catch (error) {
      console.error('Error initializing chart:', error)
    }
  }

  initializeCumulativeChart() {
    if (!this.cumulativeChartTarget || typeof Plotly === 'undefined') return
    const culmulativeChartLayout = this.getCumulativeChartLayout()
    try {
      Plotly.newPlot(this.cumulativeChartTarget, [], culmulativeChartLayout, { responsive: true })
    } catch (error) {
      console.error('Error initializing cumulative chart:', error)
    }
  }
  drawChart(chartTarget, traces, layout){
    const config = {
        responsive: true,
        displayModeBar: true,
        modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'select2d'],
        autosizable: true
    }
    
    const deduplicatedTraces = this.deduplicateLegendEntries(traces)
    
    const finalLayout = {
        ...layout,
        annotations: traces.length > 0 ? (layout.annotations || []) : [{ 
            x: 0.5, y: 0.5, xref: 'paper', yref: 'paper', 
            text: 'No data available', 
            showarrow: false, font: { size: 16, color: 'gray' },
            visible: false
        }]
    }
    
    Plotly.newPlot(chartTarget, deduplicatedTraces, finalLayout, config)
    
    chartTarget.on('plotly_click', (data) => {
      this.handleChartClick(data)
    })
  }

  handleChartClick(data) {
    if (data.points && data.points.length > 0) {
      const point = data.points[0]
      const customData = point.customdata
      
      if (customData && customData.length >= 3) {
        const isin = customData[0]
        const linkUrl = `/securities/${isin}`
        
        window.open(linkUrl, '_blank')
      }
    }
  }

  deduplicateLegendEntries(traces) {
    const seenEntities = new Set()
    const deduplicatedTraces = []
    
    traces.forEach(trace => {
      if (!seenEntities.has(trace.name)) {
        seenEntities.add(trace.name)
        deduplicatedTraces.push(trace)
      } else {
        deduplicatedTraces.push({
          ...trace,
          showlegend: false
        })
      }
    })
    
    return deduplicatedTraces
  }

  async loadInitialData() {
    try {
      await this.loadIndices()
    } catch (error) {
      this.indicesData = []
      this.updateIndexDropdown(this.indicesData)
      this.indicesLoaded = false
    }
  }

  async loadIndices() {
    if (this.indicesLoaded) return this.indicesData
    this.showLoading('market-controls')
    this.showLoading("issuance-controls")
    this.showLoading("maturities-controls")
    this.showLoading("income-controls")
    this.showLoading("cumulative-controls")
    try {
      const response = await fetch('/quant/supply_demand/indices')
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
      const indices = await response.json()
      const sortedIndices = indices.sort((a, b) => a.name.localeCompare(b.name))
      this.indicesData = sortedIndices
      this.updateIndexDropdown(sortedIndices)
      this.indicesLoaded = true
    } catch (error) {
      this.indicesLoaded = false
      this.indicesData = []
    } finally {
      this.hideLoading('market-controls')
      this.hideLoading("issuance-controls")
      this.hideLoading("maturities-controls")
      this.hideLoading("income-controls")
      this.hideLoading("cumulative-controls")
    }
  }

  updateIndexDropdown(indices) {
    const optionsList = document.querySelector('#market-index-button')?.closest('[data-controller="uikit--select-single"]')?.querySelector('[data-uikit--select-single-target="optionsList"]')
    const issuanceOptionsList = document.querySelector('#issuance-index-button')?.closest('[data-controller="uikit--select-single"]')?.querySelector('[data-uikit--select-single-target="optionsList"]')
    const cumulativeOptionsList = document.querySelector('#cumulative-index-button')?.closest('[data-controller="uikit--select-single"]')?.querySelector('[data-uikit--select-single-target="optionsList"]')
    const maturitiesOptionsList = document.querySelector('#maturities-index-button')?.closest('[data-controller="uikit--select-single"]')?.querySelector('[data-uikit--select-single-target="optionsList"]')
    const incomeOptionsList = document.querySelector('#income-index-button')?.closest('[data-controller="uikit--select-single"]')?.querySelector('[data-uikit--select-single-target="optionsList"]')
    if (!optionsList || !issuanceOptionsList || !cumulativeOptionsList) return
    const options = indices.map(index => `
      <div class="option-item flex items-center px-3 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-600 cursor-pointer"
           data-uikit--select-single-target="option" data-value="${index.id}" data-label="${index.name}" 
           data-action="click->uikit--select-single#selectOption">
        <input type="radio" name="index_radio" class="w-4 h-4 text-primary-600 bg-neutral-100 border-neutral-300 focus:ring-primary-500 dark:focus:ring-primary-600 dark:ring-offset-neutral-800 focus:ring-2 dark:bg-neutral-600 dark:border-neutral-500" 
               data-uikit--select-single-target="radio" data-action="click->uikit--select-single#selectOption click->uikit--select-single#preventPropagation">
        <label class="ml-2 text-sm text-neutral-700 dark:text-neutral-300 cursor-pointer flex-1">${index.name}</label>
      </div>
    `).join('')
    if (issuanceOptionsList) {
      issuanceOptionsList.innerHTML = options
    }
    if (cumulativeOptionsList) {
      cumulativeOptionsList.innerHTML = options
    }
    if (optionsList) {
      optionsList.innerHTML = options
    }
    if (maturitiesOptionsList) {
      maturitiesOptionsList.innerHTML = options
    }
    if (incomeOptionsList) {
      incomeOptionsList.innerHTML = options
    }
  }

  getSelectedIndexId(chartTarget='market') {
    const hiddenInput = document.querySelector(`input[name="${chartTarget}_index"]`)
    return hiddenInput ? hiddenInput.value : null
  }

  getChartParams(chartTarget='market') {
    let periodicityInput = document.querySelector(`input[name="${chartTarget}_periodicity"]`)
    if (!periodicityInput) {
      periodicityInput = document.querySelector(`select[name="${chartTarget}_periodicity"]`)
    }
    const periodicity = periodicityInput ? periodicityInput.value : 'monthly'
    
    return {
      index_id: this.getSelectedIndexId(chartTarget),
      start_date: this[`${chartTarget}StartDateTarget`]?.value || '2020-01-01',
      end_date: this[`${chartTarget}EndDateTarget`]?.value || new Date().toISOString().split('T')[0],
      periodicity: periodicity,
    }
  }

  async updateChart() {
    this.debounceChartUpdate(() => this.fetchAndUpdateChart('market'))
  }

  async updateIssuanceChart() {
    this.debounceChartUpdate(() => this.fetchAndUpdateChart('issuance'))
  }
  async updateIncomeChart() {
    this.debounceChartUpdate(() => this.fetchAndUpdateChart('income'))
  }
  async updateMaturitiesChart() {
    this.debounceChartUpdate(() => this.fetchAndUpdateChart('maturities'))
  }
  async updateCumulativeChart() {
    this.debounceChartUpdate(() => this.fetchAndUpdateCumulativeChart('cumulative'))
  }

  debounceChartUpdate(updateFn) {
    if (this.updateChartTimeout) clearTimeout(this.updateChartTimeout)
    this.updateChartTimeout = setTimeout(updateFn, 100)
  }

  async fetchAndUpdateChart(chartType='market') {
    switch(chartType) {
      case 'market':
        this.showLoading('imchart')
        this.showLoading('imiChart')
        break
      case 'issuance':
        this.showLoading('issuanceChart')
        break
      case 'maturities':
        this.showLoading('maturitiesChart')
        break
      case 'income':
        this.showLoading('incomeChart')
        break
      }

    try {
      await this.fetchChartDataAndUpdate(chartType)
    } catch (error) {
      console.error('Failed to update chart:', error)
    } finally {
      switch(chartType) {
        case 'market':
          this.hideLoading('imchart')
          this.hideLoading('imiChart')
          break
        case 'issuance':
          this.hideLoading('issuanceChart')
          break
        case 'maturities':
          this.hideLoading('maturitiesChart')
          break
        case 'income':
          this.hideLoading('incomeChart')
          break
      }
    }
  }

  async fetchAndUpdateCumulativeChart() {
    this.showLoading('cumulativeChart')
    try {
      await this.fetchCumulativeChartData()
    } catch (error) {
      console.error('Failed to update cumulative chart:', error)
    } finally {
      this.hideLoading('cumulativeChart')
    }
  }

  async fetchCumulativeChartData() {
    const indexId = this.getSelectedIndexId('cumulative')
    if (!indexId) return
    const params = this.getChartParams('cumulative')
    const cacheKey = `cumulative-${JSON.stringify(params)}`
    if (this.chartDataCache[cacheKey]) {
      this.updateCumulativeChartWithData(this.chartDataCache[cacheKey], indexId, params.periodicity)
      return
    }
    const queryString = new URLSearchParams(params).toString()
    try {
      const response = await fetch(`/quant/supply_demand/cumulative_chart_data?${queryString}`)
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
      const data = await response.json()
      this.chartDataCache[cacheKey] = data
      this.updateCumulativeChartWithData(data, indexId, params.periodicity)
    } catch (error) {
      console.error('Failed to fetch cumulative chart data:', error)
    }
  }

  updateCumulativeChartWithData(data, indexId, periodicity) {
    const plotData = this.parseCumulativeChartData(data, periodicity)
    const layout = this.getCumulativeChartLayout(periodicity)

    if (!this.cumulativeChartTarget || !indexId) {
      console.error('Cumulative chart target not found')
      return
    }
    Plotly.newPlot(this.cumulativeChartTarget, plotData, layout, { responsive: true })
  }

  async fetchChartDataAndUpdate(chartType='market') {
    const indexId = this.getSelectedIndexId(chartType)
    if (!indexId) return

    const params = this.getChartParams(chartType)
    const cacheKey = `${chartType}-${JSON.stringify(params)}`
    
    if (this.chartDataCache[cacheKey]) {
     this.updateChartWithData(this.chartDataCache[cacheKey],chartType, params.periodicity)
      return
    }

    const queryString = new URLSearchParams(params).toString()
    
    try {
      const response = await fetch(`/quant/supply_demand/chart_data?${queryString}`)
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
      
      const data = await response.json()
      this.chartDataCache[cacheKey] = data
      this.updateChartWithData(data,chartType, params.periodicity)
    } catch (error) {
      console.error('Failed to fetch chart data:', error)
    }
  }

  updateChartWithData(data,chartType='market', periodicity) {
    switch(chartType) {
      case 'market':
        const result1 = this.createStackChartTrace(data.data.maturities, 'maturity_size', data.entity_colour, false, periodicity)
        const result2 = this.createStackChartTrace(data.data.issuance, 'issue_size', data.entity_colour, true, periodicity)
        const result3 = this.createStackChartTrace(data.data.income, 'income_size', data.entity_colour, false, periodicity)
        const allPeriods = [...new Set([...result1.periods, ...result2.periods, ...result3.periods])].sort()
        this.drawChart(this.imchartTarget,[...result1.traces, ...result2.traces],this.getBarChartLayout('', 'Date', 'Size', 'market', allPeriods, periodicity))
        this.drawChart(this.imiChartTarget,[...result1.traces, ...result2.traces, ...result3.traces],this.getBarChartLayout('', 'Date', 'Size', 'market', allPeriods, periodicity))
        break
      case 'issuance':
        const issuanceResult = this.createStackChartTrace(data.data.issuance, 'issue_size', data.entity_colour, true, periodicity)
     
        this.drawChart(this.issuanceChartTarget,issuanceResult.traces,this.getBarChartLayout('', 'Date', 'Size', 'issuance', issuanceResult.periods, periodicity))
        this.updateRecentIssuancesTable()
        break
      case 'maturities':
        const maturityResult = this.createStackChartTrace(data.data.maturities, 'maturity_size', data.entity_colour, true, periodicity)
        this.drawChart(this.maturitiesChartTarget,maturityResult.traces,this.getBarChartLayout('', 'Date', 'Size', 'maturities', maturityResult.periods, periodicity))
        break
      case 'income':
        const incomeResult = this.createStackChartTrace(data.data.income, 'income_size', data.entity_colour,true, periodicity)
        this.drawChart(this.incomeChartTarget,incomeResult.traces,this.getBarChartLayout('', 'Date', 'Size', 'income', incomeResult.periods, periodicity))
        break
    }
  }
  
  parseCumulativeChartData(data, periodicity) {
    if (!data || !data.data) {
      return [{
        x: [], y: [], type: 'scatter', mode: 'lines+markers',
        name: 'No Data', line: { color: '#3b82f6', width: 2 },
        marker: { color: '#3b82f6', size: 6 }
      }]
    }
    
    const parsedData = JSON.parse(data.data)
    const legendOrder = data.legend_order || []
    const currYear = data.curr_year || new Date().getFullYear()
    
    if (!parsedData || Object.keys(parsedData).length === 0) {
      return [{
        x: [], y: [], type: 'scatter', mode: 'lines+markers',
        name: 'No Data', line: { color: '#3b82f6', width: 2 },
        marker: { color: '#3b82f6', size: 6 }
      }]
    }
    
    const monthToNumber = {
      'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
      'Jul': 7, 'Aug': 8, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12
    }
    
    const dataArray = []
    Object.keys(parsedData.date).forEach(index => {
      const monthNumber = monthToNumber[parsedData.date[index]]
      let periodNumber
      
      if (periodicity === 'quarterly') {
        periodNumber = Math.ceil(monthNumber / 3)
      } else {
        periodNumber = monthNumber
      }
      
      dataArray.push({
        date: parsedData.date[index],
        year: parsedData.year[index],
        issue_size: parsedData.issue_size[index],
        cumulative_issue_size: parsedData.cumulative_issue_size[index],
        month_number: monthNumber,
        period_number: periodNumber
      })
    })
    
    const currentMonthStr = new Date().toLocaleDateString('en-US', { month: 'short' })
    const currentMonthData = dataArray.filter(row => 
      row.year === currYear && row.date === currentMonthStr
    )
    
    const filteredData = dataArray.filter(row => 
      !(row.year === currYear && row.date === currentMonthStr)
    )
    
    const traces = []
    const yearGroups = {}
    filteredData.forEach(row => {
      if (!yearGroups[row.year]) {
        yearGroups[row.year] = []
      }
      yearGroups[row.year].push(row)
    })
    
    const sortedYears = legendOrder.length > 0 ? 
      legendOrder.filter(year => yearGroups[year]) : 
      Object.keys(yearGroups).sort((a, b) => parseInt(a) - parseInt(b))
    
    sortedYears.forEach((year, index) => {
      const yearData = yearGroups[year]
      if (!yearData || yearData.length === 0) return
      
      let processedData
      if (periodicity === 'quarterly') {
        const quarterGroups = {}
        yearData.forEach(row => {
          if (!quarterGroups[row.period_number]) {
            quarterGroups[row.period_number] = []
          }
          quarterGroups[row.period_number].push(row)
        })
        
        processedData = Object.keys(quarterGroups)
          .sort((a, b) => parseInt(a) - parseInt(b))
          .map(quarter => {
            const quarterData = quarterGroups[quarter]
            const lastMonth = quarterData.sort((a, b) => b.month_number - a.month_number)[0]
            return {
              period: parseInt(quarter),
              cumulative: lastMonth.cumulative_issue_size
            }
          })
      } else {
        yearData.sort((a, b) => a.month_number - b.month_number)
        processedData = yearData.map(row => ({
          period: row.month_number,
          cumulative: row.cumulative_issue_size
        }))
      }
      
      const trace = {
        x: processedData.map(row => row.period),
        y: processedData.map(row => row.cumulative),
        type: 'scatter',
        mode: 'lines+markers',
        name: year.toString(),
        line: { 
          color: this.colorShades[index % this.colorShades.length],
          width: 2
        },
        marker: { 
          color: this.colorShades[index % this.colorShades.length],
          size: 4
        },
        hovertemplate: `<b>${year}</b><br>${periodicity === 'quarterly' ? 'Quarter' : 'Month'}: %{x}<br>Cumulative Size: %{y:,.0f}<extra></extra>`
      }
      
      traces.push(trace)
    })
    
    if (currentMonthData.length > 0) {
      const currentMonth = currentMonthData[0]
      let currentMonthProgress
      
      if (periodicity === 'quarterly') {
        const currentDate = new Date()
        const currentMonthNumber = currentDate.getMonth() + 1 
        const currentQuarter = Math.ceil(currentMonthNumber / 3)
        const monthInQuarter = ((currentMonthNumber - 1) % 3) + 1
        const dayProgress = currentDate.getDate() / 30.0
        
        currentMonthProgress = currentQuarter - 1 + ((monthInQuarter - 1 + dayProgress) / 3.0)
      } else {
        currentMonthProgress = currentMonth.month_number - 1 + (new Date().getDate() / 30.0)
      }
      
      const currentYearData = filteredData.filter(row => row.year === currYear)
      let currentYearX = 0
      let currentYearY = 0
      
      if (currentYearData.length > 0) {
        let lastRow
        if (periodicity === 'quarterly') {
          const currentDate = new Date()
          const currentMonthNumber = currentDate.getMonth() + 1
          const currentQuarter = Math.ceil(currentMonthNumber / 3)
          
          const quarterGroups = {}
          currentYearData.forEach(row => {
            if (!quarterGroups[row.period_number]) {
              quarterGroups[row.period_number] = []
            }
            quarterGroups[row.period_number].push(row)
          })
          
          if (quarterGroups[currentQuarter]) {
            const quarterData = quarterGroups[currentQuarter]
            lastRow = quarterData.reduce((max, row) => 
              row.month_number > max.month_number ? row : max
            )
          } else {
            const lastQuarter = Math.max(...Object.keys(quarterGroups).map(Number))
            const quarterData = quarterGroups[lastQuarter]
            lastRow = quarterData.reduce((max, row) => 
              row.month_number > max.month_number ? row : max
            )
          }
        } else {
           lastRow = currentYearData.reduce((max, row) => 
            row.month_number > max.month_number ? row : max
          )
        }
        
        currentYearX = periodicity === 'quarterly' ? lastRow.period_number : lastRow.month_number
        currentYearY = lastRow.cumulative_issue_size
      }
      
      const currentMonthTrace = {
        x: [currentYearX, currentMonthProgress],
        y: [currentYearY, currentMonth.cumulative_issue_size],
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Current Month',
        line: { 
          dash: 'dot',
          color: this.colorShades[this.colorShades.length - 1],
          width: 2
        },
        marker: { 
          color: this.colorShades[this.colorShades.length - 1],
          size: 4
        },
        hovertemplate: `<b>Current Month</b><br>${periodicity === 'quarterly' ? 'Quarter' : 'Month'}: %{x}<br>Cumulative Size: %{y:,.0f}<extra></extra>`
      }
      traces.push(currentMonthTrace)
    }
    
    if (traces.length === 0) {
      return [{
        x: [], y: [], type: 'scatter', mode: 'lines+markers',
        name: 'No Data', line: { color: '#3b82f6', width: 2 },
        marker: { color: '#3b82f6', size: 6 }
      }]
    }
    
    return traces
  }

  parseJsonData(jsonString) {
    try {
      const parsed = JSON.parse(jsonString)
      if (Array.isArray(parsed)) {
        return parsed
      }
      
      const firstColumn = Object.keys(parsed)[0]
      if (!firstColumn || !parsed[firstColumn]) return []
      
      const rowIndices = Object.keys(parsed[firstColumn]).filter(key => !isNaN(parseInt(key)))
      
      if (rowIndices.length === 0) return []
      
      const dataArray = rowIndices.map(rowIndex => {
        const row = {}
        Object.keys(parsed).forEach(columnName => {
          if (parsed[columnName][rowIndex] !== undefined) {
            row[columnName] = parsed[columnName][rowIndex]
          }
        })
        return row
      })
      
      return dataArray
    } catch (error) {
      console.error('Error parsing JSON data:', error)
      return []
    }
  }

  formatXAxisLabel(date, periodicity) {
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()
    
    switch(periodicity) {
      case 'daily':
        return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      case 'weekly':
        const weekNumber = this.getWeekNumber(date)
        return `${year}-W${String(weekNumber).padStart(2, '0')}`
      case 'monthly':
        return `${year}-${String(month).padStart(2, '0')}`
      case 'quarterly':
        const quarter = Math.ceil(month / 3)
        return `${year}-Q${quarter}`
      case 'half-yearly':
        const half = month <= 6 ? 'H1' : 'H2'
        return `${year}-${half}`
      case 'yearly':
        return `${year}`
      default:
        return `${year}-${String(month).padStart(2, '0')}`
    }
  }

  getWeekNumber(date) {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()))
    const dayNum = d.getUTCDay() || 7
    d.setUTCDate(d.getUTCDate() + 4 - dayNum)
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7)
  }

  generateYearlyTickPositions(dataPeriods, periodicity = 'monthly') {
    if (!dataPeriods || dataPeriods.length === 0) return []
    
    const yearlyPositions = []
    const years = new Set()
    
    dataPeriods.forEach((period, index) => {
      const year = period.split('-')[0]
      if (!years.has(year)) {
        years.add(year)
        const date = this.parsePeriodLabel(period, periodicity)
        yearlyPositions.push(date)
      }
    })
    
    return yearlyPositions
  }

  getYearStartPeriod(year, periodicity) {
    switch(periodicity) {
      case 'daily':
        return `${year}-01-01`
      case 'weekly':
        return `${year}-W01`
      case 'monthly':
        return `${year}-01`
      case 'quarterly':
        return `${year}-Q1`
      case 'half-yearly':
        return `${year}-H1`
      case 'yearly':
        return `${year}`
      default:
        return `${year}-01`
    }
  }

  addYearStartPeriods(dataPeriods, periodicity) {
    const years = new Set()
    const yearStartPeriods = []
    
    dataPeriods.forEach(period => {
      const year = period.split('-')[0]
      years.add(year)
    })
    
    years.forEach(year => {
      const yearStart = this.getYearStartPeriod(year, periodicity)
      yearStartPeriods.push(yearStart)
    })
    
    const allPeriods = [...new Set([...dataPeriods, ...yearStartPeriods])].sort((a, b) => {
      return this.comparePeriodLabels(a, b, periodicity)
    })
    
    return allPeriods
  }

  generateYearlyTickLabels(dataPeriods, periodicity = 'monthly') {
    if (!dataPeriods || dataPeriods.length === 0) return []
    
    const yearlyLabels = []
    const years = new Set()
    
    dataPeriods.forEach(period => {
      const year = period.split('-')[0]
      if (!years.has(year)) {
        years.add(year)
        const label = this.formatYearlyLabel(year, periodicity)
        yearlyLabels.push(label)
      }
    })
    
    return yearlyLabels
  }

  formatYearlyLabel(year, periodicity) {
    switch(periodicity) {
      case 'daily':
        return `${year}-01-01`
      case 'weekly':
        return `${year}-W01`
      case 'monthly':
        return `${year}-01`
      case 'quarterly':
        return `${year}-Q1`
      case 'half-yearly':
        return `${year}-H1`
      case 'yearly':
        return `${year}`
      default:
        return `${year}-01`
    }
  }

  comparePeriodLabels(a, b, periodicity) {
    const dateA = this.parsePeriodLabel(a, periodicity)
    const dateB = this.parsePeriodLabel(b, periodicity)
    return dateA - dateB
  }

  parsePeriodLabel(periodLabel, periodicity) {
    switch(periodicity) {
      case 'daily':
        return new Date(periodLabel)
      case 'weekly':
        const [yearW, week] = periodLabel.split('-W')
        const year = parseInt(yearW)
        const weekNum = parseInt(week)
        const jan1 = new Date(year, 0, 1)
        const weekStart = new Date(jan1.getTime() + (weekNum - 1) * 7 * 24 * 60 * 60 * 1000)
        return weekStart
      case 'monthly':
        const [yearM, month] = periodLabel.split('-')
        return new Date(parseInt(yearM), parseInt(month) - 1, 1)
      case 'quarterly':
        const [yearQ, quarter] = periodLabel.split('-Q')
        const quarterMonth = (parseInt(quarter) - 1) * 3
        return new Date(parseInt(yearQ), quarterMonth, 1)
      case 'half-yearly':
        const [yearH, half] = periodLabel.split('-H')
        const halfMonth = half === '1' ? 0 : 6
        return new Date(parseInt(yearH), halfMonth, 1)
      case 'yearly':
        return new Date(parseInt(periodLabel), 0, 1)
      default:
        return new Date(periodLabel)
    }
  }

  createStackChartTrace(data, sizeField, colors, positive, periodicity){
    const processedData = JSON.parse(data)
    const rows=[]


    Object.keys(processedData.isin).forEach(i=>{
      const fullDate = new Date(parseInt(processedData.date[i]))
      
      rows.push({
        isin: processedData.isin[i],
        size: processedData[sizeField][i],
        date: fullDate,
        entity: processedData.entity[i]
      })
    })
    
    const sortedRows = rows.sort((a, b) => a.date - b.date)
    
    const dataPeriods = [...new Set(sortedRows.map(r => {
      const date = new Date(r.date)
      return this.formatXAxisLabel(date, periodicity)
    }))].sort((a, b) => {
      return this.comparePeriodLabels(a, b, periodicity)
    })
    
    const uniquePeriods = this.addYearStartPeriods(dataPeriods, periodicity)
    const isinGroups = {}
    sortedRows.forEach(row => {
      const key = `${row.entity}-${row.isin}`
      if (!isinGroups[key]) {
        isinGroups[key] = {
          entity: row.entity,
          isin: row.isin,
          rows: []
        }
      }
      isinGroups[key].rows.push(row)
    })
    
    const traces = Object.values(isinGroups).map((group, index) => {
      const periodDataMap = new Map()
      group.rows.forEach(row => {
        const date = new Date(row.date)
        const period = this.formatXAxisLabel(date, periodicity)
        if (!periodDataMap.has(period)) {
          periodDataMap.set(period, [])
        }
        periodDataMap.get(period).push(row)
      })
      
      const xValues = []
      const yValues = []
      const customDataValues = []
      
      uniquePeriods.forEach(period => {
        const date = this.parsePeriodLabel(period, periodicity)
        xValues.push(date)
        const periodRows = periodDataMap.get(period) || []
        if (periodRows.length > 0) {
          const row = periodRows[0]
          yValues.push(positive ? row.size : -row.size)
          customDataValues.push([row.isin, row.entity, positive ? row.size : -row.size])
        } else {
          yValues.push(0)
          customDataValues.push([null, group.entity, 0])
        }
      })
      
      const entityColor = colors[group.entity] || this.colorShades[index % this.colorShades.length]
      
      return {
        x: xValues,
        y: yValues,
        name: group.entity,
        type: 'bar',
        marker: { color: entityColor },
        hovertemplate:
          `<b>ISIN: %{customdata[0]}</b><br>` + 
          `Entity: %{customdata[1]}<br>` +
          `Size: %{customdata[2]:,}<br>` +
          `Date: %{x}<extra></extra>`,
        customdata: customDataValues,
        showlegend: true,
        legendgroup: group.entity,
      }
    })
    return { traces, periods: uniquePeriods }
  }

  createEntityTraces(data, prefix) {
    const entityGroups = {}
    data.forEach(row => {
      if (!entityGroups[row.entity]) entityGroups[row.entity] = []
      entityGroups[row.entity].push(row)
    })
    const sortedEntityGroups = Object.keys(entityGroups).sort((a, b) => a.legendgroup - b.legendgroup)

    return sortedEntityGroups.map(entity => {
      const groupData = entityGroups[entity]
      
      return {
        x: groupData.map(d => d.x),
        y: groupData.map(d => d.y),
        type: 'bar',
        marker: {
          color: groupData[0].entity_colour,
        },
        customdata: groupData.map(d => ({
          isin: d.isin,
          entity: d.entity,
          size: Math.abs(d.size || d.y)
        })),
        hovertemplate: `Type: ${prefix}<br>` +
                       `ISIN: %{customdata.isin}<br>` +
                       `Entity: %{customdata.entity}<br>` +
                       `Size: %{customdata.size:,.0f}<br>` +
                       `Date: %{x}<extra></extra>`,
        showlegend: true,
        offsetgroup: entity
      }
    })
  }

  generateColor(entity) {
    let hash = 0
    for (let i = 0; i < entity.length; i++) {
      hash = entity.charCodeAt(i) + ((hash << 5) - hash)
    }
    const hue = Math.abs(hash) % 360
    return `hsl(${hue}, 70%, 50%)`
  }

  getBarChartLayout(title = '',xTitle = 'Date',yTitle = 'Size',chartType = 'market', dataPeriods = [], periodicity = 'monthly') {
    const params = this.getChartParams(chartType)
    return {
      title: { text: title, font: { size: 16, color: '#333' } },
      xaxis: { 
        title: xTitle,
        titlefont: { size: 14, color: '#333' },
        type: 'date',
        tickfont: { size: 12, color: '#666' },
        showgrid: false,
        zeroline: true,
        zerolinewidth: 1,
        zerolinecolor: '#000000',
        dividerwidth: 1,
        dividercolor: '#000000',
        tickmode: 'array',
        tickvals: this.generateYearlyTickPositions(dataPeriods, periodicity),
        ticktext: this.generateYearlyTickLabels(dataPeriods, periodicity)
      },
      yaxis: { 
        title: yTitle,
        zeroline: false
      },
      margin: { l: 80, r: 150, t: 60, b: 60 },
      barmode: 'relative',
      bargap: 0.05,
      bargroupgap: 0.15,
      hovermode: 'closest',
      font: { family: 'Arial, sans-serif', size: 12, color: '#333' },
      autosize: true,
      responsive: true
    }
  }

  getCumulativeChartLayout(periodicity = 'monthly') {
    let xaxisConfig
    if (periodicity === 'quarterly') {
      xaxisConfig = {
        title:"Period",
        range: [1, 4],
        tickvals: [1, 2, 3, 4],
        ticktext: ['Q1', 'Q2', 'Q3', 'Q4'],
        showgrid: true,
        zeroline: true,
        zerolinewidth: 1,
        gridcolor: '#e5ecf6'
      }
    } else {
      xaxisConfig = {
        title:"Period",
        range: [1, 12],
        tickvals: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        ticktext: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
        showgrid: true,
        zeroline: true,
        zerolinewidth: 1,
        gridcolor: '#e5ecf6'
      }
    }
    
    return {
      title: { text: '', font: { size: 16 } },
      xaxis: xaxisConfig,
      yaxis: { 
        title:"Cumulative Size",
        showgrid: true,
        zeroline: true,
        zerolinewidth: 1,
        gridcolor: '#e5ecf6',
        tickformat: '.2s'
      },
      showlegend: true,
      legend: { orientation: 'v', x: 1.02, y: 1, xanchor: 'left' },
      autosize: true,
      height: 500,
      margin: { l: 40, r: 150, t: 40, b: 40 },
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
      font: { color: '#333' },
      hovermode: 'closest',
      hoverlabel: { namelength: -1 },
      responsive: true
    }
  }

  showLoading(target) {
    const card = document.querySelector(`[data-loading-target="${target}"]`)
    const spinner = card?.querySelector('.loading-spinner')
    if (spinner) spinner.classList.remove('hidden')
  }

  hideLoading(target) {
    const card = document.querySelector(`[data-loading-target="${target}"]`)
    const spinner = card?.querySelector('.loading-spinner')
    if (spinner) spinner.classList.add('hidden')
  }

  sortDataByDate(data) {
    return data.sort((a, b) => {
      const dateA = new Date(a.date)
      const dateB = new Date(b.date)
      return dateB - dateA
    })
  }

  async exportToCSV(event) {
    const {chartType, csvButtonId} = event.params
    const params = this.getChartParams(chartType)
    const indexId = this.getSelectedIndexId(chartType)
    
    if (!indexId) {
      console.error('Index not found')
      return
    }

    const exportButton = document.getElementById(csvButtonId)
    if (!exportButton) {
      console.error('Export button not found')
      return
    }
    this.setButtonLoading(exportButton, true)
    try {
      const cacheKey = `${chartType}-${JSON.stringify(params)}`
      const chartData = this.chartDataCache[cacheKey]
      
      if (!chartData) {
        this.setButtonLoading(exportButton, false)
        return
      }
      
      const csvContent = this.generateCSVFromChartData(chartData,chartType)
      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${chartType}_${indexId.substring(0, 8)}_${new Date().toISOString().split('T')[0]}.csv`
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      this.setButtonLoading(exportButton, false)
      
    } catch (error) {
      console.error('Failed to export CSV:', error)
      this.setButtonLoading(exportButton, false)
      alert(`Failed to export data: ${error.message}. Please try again.`)
    }
  }

  generateCSVFromChartData(data,chartType) {
    if (!data || !data.data || !chartType) {
      return 'No data available'
    }
    
    const csvRows = []
    if (chartType === 'cumulative') {
      csvRows.push(`${chartType},Date,Year,Issue Size,Cumulative Issue Size,Month Number`)
    }
    else {
      csvRows.push(`${chartType},Date,Entity,ISIN,Size`)
    }
    switch (chartType) {
      case 'cumulative':
        csvRows.push(...this.generateCSVDataFromCumulative(data))
        break
      case 'issuance':
        csvRows.push(...this.generateCSVFromData(data.data.issuance,'issuance','issue_size'))
        break
      case 'maturities':
        csvRows.push(...this.generateCSVFromData(data.data.maturities,'maturities','maturity_size'))
        break
      case 'income':
        csvRows.push(...this.generateCSVFromData(data.data.income,'income','income_size'))
        break
      case 'market':
        csvRows.push(...this.generateCSVFromData(data.data.maturities,'maturities','maturity_size'))
        csvRows.push(...this.generateCSVFromData(data.data.issuance,'issuance','issue_size'))
        csvRows.push(...this.generateCSVFromData(data.data.income,'income','income_size'))
        break
    }
    return csvRows.join('\n')
  }

  generateCSVFromData(data,chartType = "market", sizeField = "issue_size") {
    const csvRows = []
    const parsedData = this.parseJsonData(data)
    const sortedData = this.sortDataByDate(parsedData)
    sortedData.forEach(row => {
      if (row.date && row[sizeField] !== null && row[sizeField] !== undefined) {
        const date = new Date(parseInt(row.date)).toISOString().split('T')[0]
        csvRows.push(`${chartType==='market' ? 'Market Supply' : chartType},${date},"${row.entity}","${row.isin}",${row[sizeField]}`)
      }
    })
    return csvRows
  }
  generateCSVDataFromCumulative(data) {
    const csvRows = []
    const rows = this.parseJsonData(data.data)
    if (!rows || rows.length === 0) return csvRows

    const monthToNumber = {
      Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
      Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12
    }

    const sortedRows = rows.sort((a, b) => {
      const yearA = parseInt(a.year)
      const yearB = parseInt(b.year)
      if (yearA !== yearB) return yearB - yearA
      
      const monthA = monthToNumber[a.date]
      const monthB = monthToNumber[b.date]
      return monthB - monthA
    })

    sortedRows.forEach(row => {
      const dateLabel = row.date
      const year = row.year
      const issueSize = row.issue_size
      const cumulativeIssueSize = row.cumulative_issue_size
      const monthNumber = monthToNumber[dateLabel] || ''

      if (
        dateLabel !== undefined &&
        year !== undefined &&
        issueSize !== undefined &&
        cumulativeIssueSize !== undefined
      ) {
        csvRows.push(`cumulative,${dateLabel},${year},${issueSize},${cumulativeIssueSize},${monthNumber}`)
      }
    })
    return csvRows
  }

  setButtonLoading(button, isLoading) {
    if (!button) return
    
    if (isLoading) {
      button.disabled = true
      button.classList.add('opacity-60', 'pointer-events-none')
      
      const icon = button.querySelector('i')
      if (icon) {
        icon.className = 'fa-solid fa-spinner animate-spin mr-2'
      }
      
      const textSpan = button.querySelector('span')
      if (textSpan) {
        textSpan.textContent = 'Exporting...'
      }
    } else {
      button.disabled = false
      button.classList.remove('opacity-60', 'pointer-events-none')
      
      const icon = button.querySelector('i')
      if (icon) {
        icon.className = 'fa-solid fa-download mr-2'
      }
      
      const textSpan = button.querySelector('span')
      if (textSpan) {
        textSpan.textContent = 'Export CSV'
      }
    }
  }

  refreshAll(event) {
    const { type } = event.params;
    this.chartDataCache = {}
    this.issuanceTabPreloaded = false
    this.maturitiesTabPreloaded = false
    if (type === 'market') {
      this.resetControls(this.marketStartDateTarget,this.marketEndDateTarget,'market-periodicity','market-index')
      this.resetChartWithTarget(this.imchartTarget)
      this.resetChartWithTarget(this.imiChartTarget)
    }
    if (type==='income') {
      this.resetControls(this.incomeStartDateTarget,this.incomeEndDateTarget,'income-periodicity','income-index')
      this.resetChartWithTarget(this.incomeChartTarget)
    }
    if (type==='maturities') {
      this.resetControls(this.maturitiesStartDateTarget,this.maturitiesEndDateTarget,'maturities-periodicity','maturities-index')
      this.resetChartWithTarget(this.maturitiesChartTarget)
    }
    if (type==='issuance') {
      this.resetControls(this.issuanceStartDateTarget,this.issuanceEndDateTarget,'issuance-periodicity','issuance-index')
      this.resetChartWithTarget(this.issuanceChartTarget)
      this.initializeTables()
    }
    if (type==='cumulative') {
      this.resetControls(this.cumulativeStartDateTarget,this.cumulativeEndDateTarget,'cumulative-periodicity','cumulative-index')
      this.resetChartWithTarget(this.cumulativeChartTarget)
    }
  }
  resetChartWithTarget(target){
    if (target ===this.cumulativeChartTarget){
      this.initializeCumulativeChart()
    }
    else {
      this.initializeChart(target)
    }
  }

  resetControls(startDateTarget,endDateTarget,periodicityTarget,indexTarget) {
    if (startDateTarget) {
      startDateTarget.value = '2020-01-01'
    }
    
    if (endDateTarget) {
      endDateTarget.value = new Date().toISOString().split('T')[0]
    }
    const periodicityRoot = document
    .querySelector(`#${periodicityTarget}-button`)
    ?.closest('[data-controller="uikit--select-single"]')
    if (periodicityRoot) {
      const periodicityController = this.application.getControllerForElementAndIdentifier(
        periodicityRoot,
        'uikit--select-single'
      )
      periodicityController?.setSelectedValue('monthly')
    }
    const indexRoot = document
      .querySelector(`#${indexTarget}-button`)
      ?.closest('[data-controller="uikit--select-single"]')
    if (indexRoot) {
      const indexController = this.application.getControllerForElementAndIdentifier(
        indexRoot,
        'uikit--select-single'
      )
      indexController?.clear()
    }
  }

  initializeTables() {
    if (this.recentIssuancesGrid) this.recentIssuancesGrid.destroy()
    this.recentIssuancesGrid = this.createSlickGrid(this.recentIssuancesTableTarget, [], this.getRecentIssuancesColumns())
  }

  updateRecentIssuancesTable() {
    const params = this.getChartParams('issuance')
    const cacheKey = `issuance-${JSON.stringify(params)}`
    const data = this.chartDataCache[cacheKey]
    if (!data) return
    const issuanceData = this.parseJsonData(data.data.issuance)
    if (!issuanceData) return
    const filteredData = issuanceData.filter(row => row.date && row.issue_size !== null && row.issue_size !== undefined)
    .map(row => ({
      date: new Date(row.date).toISOString().split('T')[0],
      isin: row.isin,
      name: row.name || '-',
      amount: row.issue_size || '-',
      issuer: row.entity || '-',
      currency: row.currency || '-',
      price_method: row.price_method || '-',
      rating: row.rating || '-',
      tenor: row.tenor || '-',
      margin: row.margin || '-'
    }))
    if (!filteredData) return
    const columns = this.getRecentIssuancesColumns()
    this.recentIssuancesGrid = this.createSlickGrid(this.recentIssuancesTableTarget, filteredData, columns)
  }

  getRecentIssuancesColumns() {
    return [
      { id: 'date',    name: 'Date',    field: 'date',    minWidth: 120 },
      { id: 'isin',    name: 'ISIN',    field: 'isin',    minWidth: 140 },
      { id: 'name',    name: 'Name',    field: 'name',    minWidth: 220 },
      { id: 'amount',  name: 'Amount',  field: 'amount',  minWidth: 120 },
      { id: 'issuer',  name: 'Issuer',  field: 'issuer',  minWidth: 200 },
      { id: 'currency',name: 'Currency',field: 'currency',minWidth: 90  },
      { id: 'price',   name: 'Price Method', field: 'price_method', minWidth: 150 },
      { id: 'rating',  name: 'Rating',  field: 'rating',  minWidth: 100 },
      { id: 'tenor',   name: 'Tenor',   field: 'tenor',   minWidth: 100 },
      { id: 'margin',  name: 'Issue Margin/Coupon Rate', field: 'margin', minWidth: 200 },
    ]
  }

  createSlickGrid(target, data, columns, options = {}) {
    const sortedData = data.sort((a, b) => {
      const dateA = new Date(a.date)
      const dateB = new Date(b.date)
      return dateB - dateA
    })

    const defaultOptions = { 
      rowHeight: 32, 
      forceFitColumns: true,
      enableColumnReorder: true,
      enableTextSelectionOnCells: true,
      enableCellNavigation: true,
      autoEdit: false
    }
    const grid = new SlickGrid(target, sortedData, columns, { ...defaultOptions, ...options })
    const style = document.createElement('style')
    style.textContent = `
      .slick-cell {
        display: flex !important;
        align-items: center !important;
        vertical-align: middle !important;
        line-height: 1 !important;
      }
      .slick-cell input {
        margin: 0 !important;
        vertical-align: middle !important;
      }
    `
    document.head.appendChild(style)
    setTimeout(() => { grid.resizeCanvas() }, 100)
    return grid
  }
}
